import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ChevronRight } from 'lucide-react';
import { Card, Button } from './V2Components';
import UsageBar from './UsageBar';
import SubscriptionService from '../../services/SubscriptionService';
import { useUpgrade } from './PaywallProvider';

// A compact subset for the Profile page; the full breakdown lives on /subscription.
const KEY_CATEGORIES = [
  { key: 'items', label: 'Items' },
  { key: 'storage', label: 'Storage', isStorage: true },
  { key: 'scans', label: 'Document scans' },
];

/**
 * Compact plan + usage summary for the Profile page. Free users get an Upgrade
 * button that reuses the app-wide paywall modal (useUpgrade); paid users get a
 * link into the dedicated /subscription page (which has the billing-portal action).
 * Fails quietly — billing is non-critical to the Profile page.
 */
export default function PlanUsageCard() {
  const navigate = useNavigate();
  const { openUpgrade } = useUpgrade();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    SubscriptionService.getLimits()
      .then((d) => {
        if (active) setData(d);
      })
      .catch((err) => {
        // Plan/usage is secondary on the Profile page — fail quietly, but leave a
        // dev breadcrumb so a broken load isn't completely invisible.
        if (import.meta.env?.DEV) console.error('PlanUsageCard: failed to load limits', err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <Card className="p-6">
        <p className="text-sm text-[var(--text-secondary)]">Loading your plan…</p>
      </Card>
    );
  }

  // Load finished but failed (data still null): omit the secondary card rather
  // than render a perpetual "Loading…" state.
  if (!data) {
    return null;
  }

  const isPaid = data.subscription_tier === 'paid';

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles size={20} className="text-[var(--primary)]" />
          <div>
            <p className="font-bold text-[var(--text-main)]">{isPaid ? 'Premium' : 'Free'} plan</p>
            <p className="text-xs text-[var(--text-secondary)] capitalize theme-transition">
              {data.subscription_status}
            </p>
          </div>
        </div>
        {isPaid ? (
          <Button variant="secondary" size="sm" onClick={() => navigate('/subscription')}>
            Manage
          </Button>
        ) : (
          <Button variant="primary" size="sm" leadingIcon={Sparkles} onClick={() => openUpgrade()}>
            Upgrade
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {KEY_CATEGORIES.map(({ key, label, isStorage }) => {
          const cat = data.categories?.[key];
          if (!cat) return null;
          return (
            <UsageBar
              key={key}
              label={label}
              usage={cat.usage}
              limit={cat.limit}
              percentage={cat.percentage}
              isStorage={isStorage}
              note={key === 'scans' ? cat.type : undefined}
            />
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => navigate('/subscription')}
        className="flex items-center justify-between w-full text-sm text-[var(--primary)] hover:underline"
      >
        View plan &amp; usage
        <ChevronRight size={16} />
      </button>
    </Card>
  );
}
